import {SessionResult, InputType, ForceLogoutErrorCodes} from '@/constants'
import {getErrorData, setContentWidth} from '@/utils'
import {DefaultCustomerPortalSettings} from '../constants'

function setError(state, error) {
  state.status = 'error'
  state.messages = []

  setTimeout(() => {
    if (typeof error === 'string') {
      state.messages = [error]
      state.error = null
    } else if (Array.isArray(error)) {
      state.messages = error.slice()
      state.error = null
    } else {
      state.error = error
    }
  }, 0)
}

function setCriticalError(state, error) {
  const err = getErrorData(error)
  console.log(err.message)
  state.isLoading = false

  if (typeof state.lifelineCallback === 'function') {
    state.criticalError = error
    state.lifelineCallback(SessionResult.Error, err.errorString)
  } else {
    setError(state, err.errors)
  }
}

function clearError(state) {
  state.criticalError = null
  state.error = null
}

function logout(state) {
  state.status = ''
  state.token = ''
  state.session = null
  state.popupDisplayState = null
  state.isLoading = false
  state.initialLoading = false
  state.selectedItems = []
  state.selectedItemIndex = 0
  state.startIndex = 0
  state.undoCount = 0
  state.error = null
  state.criticalError = null
  state.messages = []
  state.isMatchVerifyError = []
  state.selectedFaqTopic = null
  state.cart = []
  state.checkoutErrors = null
  state.forceDisplayLoader = false
  state.popupSession = null
  state.isPopupLoading = false
  state.stepStack = []
  localStorage.removeItem('chat')
  sessionStorage.removeItem('orderDetail')

  if (state.idleTimer) {
    clearTimeout(state.idleTimer)
  }

  console.log('logout')
}

const mutations = {
  auth_request(state) {
    state.token = ''
    state.popupDisplayState = null
    state.isLoading = true
    console.log('auth request')
  },
  auth_success(state, token) {
    state.status = 'success'
    state.token = token
    state.isLoading = false
    clearError(state)
    console.log('auth success')
  },
  auth_error(state) {
    state.token = ''
    state.session = null
    state.popupDisplayState = null
    console.log('auth error')
  },
  logout(state) {
    logout(state)
  },
  session(state, sess) {
    state.firstLogin = !state.session // only show welcome message at the start of the session
    if (sess.is_popup) {
      state.popupSession = {...sess}
      let session = {...state.session}

      if (!state.session) {
        session = {...sess}
        // if the first step is popup, we should clear content of background session so it does not display double step
        if (session.is_first_step) {
          session.label = null
          session.content = null
          session.valid_inputs = []
          session.hide_all_buttons = true
        }
      }
      // if current step is popup step, just set session.is_popup true and popup data will be displayed from separate object
      session.is_popup = true
      // this flag on session will help us differentiate between popup session and background session in Session.vue
      // it is implemented to fix issue with popup session that the old step should stay same in background
      session.is_background_session = true
      session.style = (session.style || '') + (sess.style || '')
      session.status = sess.status
      session.required_fields = sess.required_fields
      session.can_undo = sess.can_undo
      state.session = session
    } else {
      state.session = sess
      state.popupSession = null
      state.session.is_background_session = false
    }

    if (sess.campaign && (!state.campaign || state.firstLogin || state.campaign.id !== sess.campaign.id)) {
      state.campaign = sess.campaign
      state.style = sess.style
      state.styleUrl = sess.campaign.style_url
      state.styleVariables = sess.campaign.style_variables
      state.styleLabels = sess.campaign.style_labels
      state.faviconUrl = sess.campaign.favicon ? sess.campaign.favicon.file : null

      if (process.env.VUE_APP_ENABLE_MOUSEFLOW === 'true' && state.campaign.mouseflow_enabled) {
        setTimeout(() => {
          window._mfq = window._mfq || []
          window._mfq.push(['setVariable', 'session', sess.id.toString()])
          window._mfq.push(['setVariable', 'campaign', sess.campaign.id.toString()]);

          (function() {
            let mouseFlowUrl = 'https://cdn.mouseflow.com/projects/e6f1d1cd-3b76-4302-b0e3-2851720f042c.js'
            let alreadyAdded = false
            let scripts = document.getElementsByTagName('script')
            for (let i = 0; i < scripts.length; i++) {
              if (scripts[i].src === mouseFlowUrl) {
                alreadyAdded = true
                break
              }
            }

            if (!alreadyAdded) {
              let mf = document.createElement('script')
              mf.type = 'text/javascript'
              mf.defer = true
              mf.src = mouseFlowUrl
              document.getElementsByTagName('head')[0].appendChild(mf)
            }
          })()
        }, 500)
      }
    }

    // update customer portal settings
    if (sess && sess.customer_portal_settings) {
      state.customerPortalSettings = sess.customer_portal_settings
    } else {
      state.customerPortalSettings = DefaultCustomerPortalSettings
    }

    // update status messages
    if (sess && sess.status_messages && sess.status_messages.length && sess.step_type !== InputType.ItemStatus) {
      state.messages = sess.status_messages.slice()
    }

    // display warning messages as errors
    if (sess.warning_messages && sess.warning_messages.length) {
      setError(state, sess.warning_messages.slice())
    }

    // set content widths
    setContentWidth(sess.content_config)
    if (sess.content_config) {
      state.isBannerHidden = sess.content_config.banner.is_hidden || false
      state.isLabelHidden = sess.content_config.label.is_hidden || false
      state.isContentHidden = sess.content_config.content.is_hidden || false
      state.isMediaHidden = sess.content_config.is_hidden || false
    }
  },
  switchable_campaigns(state, campaigns) {
    state.campaign.switchable_campaigns = campaigns
  },
  customer(state, cust) {
    state.session.customer = cust
  },
  payment_sources(state, sources) {
    state.payment_sources = sources
  },
  payment_source(state, source) {
    // update the customer's matching payment source with the new one
    for (let i = 0; i < state.payment_sources.length; i++) {
      if (state.payment_sources[i].id === source.id) {
        state.payment_sources[i] = source
        break
      }
    }
  },
  addresses(state, addresses) {
    state.session.customer.addresses = addresses
  },
  address(state, address) {
    // update the customer's matching address with the new one
    for (let i = 0; i < state.session.customer.addresses.length; i++) {
      if (state.session.customer.addresses[i].id === address.id) {
        state.session.customer.addresses[i] = address
        break
      }
    }
  },
  error(state, err) {
    setError(state, err)
    state.isLoading = false
    console.log(err)
  },
  success(state, action = null) {
    state.status = 'success'
  },
  messages(state, messages) {
    if (typeof messages === 'object' && messages.messages) {
      state.messages = messages.messages.slice()
      if (messages.autoHide) {
        setTimeout(() => {
          state.messages = []
        }, 3000)
      }
    } else if (messages) {
      state.messages = messages.slice()
    }

    if (messages.status) {
      state.status = messages.status
    }

    if (messages.isMatchVerifyError) {
      state.isMatchVerifyError = messages.isMatchVerifyError
    }
  },
  busy(state) {
    if (state.session && state.session.is_popup) {
      state.isPopupLoading = true
    } else {
      state.isLoading = true
    }
    console.log('busy')
  },
  showCPLoader(state) {
    state.isCPLoading = true
  },
  hideCPLoader(state) {
    if (!state.forceDisplayLoader) {
      state.isCPLoading = false
    }
  },
  ready(state) {
    if (!state.forceDisplayLoader) {
      state.isLoading = false
      console.log('ready')
      state.isPopupLoading = false
    }
  },
  hide_welcome_message(state) {
    state.firstLogin = false
  },
  clear_selected_items(state) {
    state.selectedItems = []
    state.selectedItemIndex = 0
    state.startIndex = 0
  },
  set_selected_items(state, items) {
    state.selectedItems = items.slice(0)
    state.selectedItemIndex = 0
    state.startIndex = 0
  },
  set_status_items(state, items) {
    state.statusItems = items.slice(0)
  },
  set_display_state(state, displayState) {
    state.session.is_popup = !displayState
    state.popupDisplayState = displayState
  },
  previous_selected_item(state) {
    if (state.selectedItemIndex >= state.startIndex) {
      state.selectedItemIndex--
    }
  },
  next_selected_item(state) {
    if (state.selectedItemIndex < state.selectedItems.length) {
      state.selectedItemIndex++
    }
  },
  remove_selected_item(state, item) {
    let newSelection = []

    for (let i = 0; i < state.selectedItems; i++) {
      if (state.selectedItems[i].id === item.id) {
        if (state.selectedItemIndex > i) {
          state.selectedItemIndex-- // adjust the selected index item if we're removing an element below it
        }
      } else {
        newSelection.push(state.selectedItems[i])
      }
    }

    state.selectedItems = newSelection
  },
  update_selected_item(state) {
    // update the matching item in the selected item list from the item on the session
    if (state.session.selected_item) {
      if (state.selectedItems.length) {
        let selectedItems = state.selectedItems.slice(0)

        for (let i = 0; i < selectedItems.length; i++) {
          if (selectedItems[i].id === state.session.selected_item.id) {
            if (state.session.new_item) {
              selectedItems[i] = state.session.new_item // selected item was replace by a new item
            } else {
              selectedItems[i] = state.session.selected_item // selected item is the same item (modified)
            }

            if (!state.session.can_undo) {
              state.startIndex = i + 1 // if we can't undo the session, then we can't go back to any previous items
            }

            break
          }
        }

        state.selectedItems = selectedItems.slice(0)
      } else if (state.session.auto_select_single_item) {
        state.selectedItems = [state.session.selected_item] // single item was auto selected
      }
    }
  },
  select_item(state, item) {
    // set the selected item index to match the selected item
    for (let i = 0; i < state.selectedItems.length; i++) {
      if (state.selectedItems[i].id === item.id) {
        if (i < state.startIndex) {
          state.startIndex = i // ensure item is visible
        }

        state.selectedItemIndex = i
        break
      }
    }
  },
  reset_idle_timer(state) {
    // reset the lifeline timer
    if (state.idleTimer) {
      clearTimeout(state.idleTimer)
    }

    if ((typeof state.lifelineCallback === 'function') &&
        !!state.session &&
        state.session.lifeline_timeout &&
        state.session.can_enter_lifeline) {
      state.idleTimer = setTimeout(function() {
        console.log('lifeline timeout')
        state.lifelineCallback(SessionResult.LifelineTimeout)
      }, state.session.lifeline_timeout * 1000)
    }
  },
  increment_undo_count(state) {
    if (!!state.session && state.session.can_enter_lifeline) {
      state.undoCount++

      if ((typeof state.lifelineCallback === 'function') &&
        state.session.lifeline_undo_count &&
        (state.undoCount >= state.session.lifeline_undo_count)) {
        state.lifelineCallback(SessionResult.LifelineUndo)
      }
    }
  },
  no_match(state, message, result = SessionResult.LifelineNoMatch) {
    if (typeof state.lifelineCallback === 'function') {
      if (!!state.session && state.session.can_enter_unmatched_lifeline) {
        setError(state, message)
      } else {
        state.isLoading = false
      }
      state.criticalError = {response: {data: {error_message: message}}}
      console.log(state.criticalError)
      state.lifelineCallback(result)
    } else {
      setError(state, message)
    }
  },
  exit_popup(state) {
    if (!!state.session && state.session.can_enter_exit_lifeline) {
      if (typeof state.lifelineCallback === 'function') {
        state.lifelineCallback(SessionResult.LifelineExit)
      }
    }
  },
  auth_critical_error(state, error) {
    setCriticalError(state, error)
  },
  critical_error(state, error) {
    if (error.response && (ForceLogoutErrorCodes.indexOf(error.response.status) >= 0)) {
      logout(state) // invalid session so logout
    } else {
      if (state.session && state.session.can_enter_error_lifeline && (typeof state.lifelineCallback === 'function')) {
        const err = getErrorData(error)
        state.lifelineCallback(SessionResult.LifelineError, err.errorString)
        state.error = error
      } else {
        setCriticalError(state, error)
      }
    }
  },
  checkout_error(state, error) {
    state.checkoutErrors = error
    state.isLoading = false
  },
  clear_checkout_error(state) {
    state.checkoutErrors = null
  },
  lifeline_error(state, error) {
    // error trying to set a lifeline, so it's a critical error, but don't try updating the server again
    state.isLoading = false
    state.criticalError = error
  },
  reset_lifeline(state) {
    state.undoCount = 0
    state.criticalError = null
    state.status = 'success'
  },
  set_lifeline_function(state, func) {
    state.lifelineCallback = func
  },
  loading_faq_data(state) {
    state.faqsLoading = true
    state.allFaqTopics = []
  },
  set_faq_topics(state, topics) {
    state.allFaqTopics = topics
    state.faqsLoading = false
  },
  clear_messages(state) {
    state.error = null
    state.messages = []
  },
  clear_error(state) {
    state.error = null
  },
  profiler_enable(state, enable) {
    state.profilerEnabled = enable
  },
  cprofiler_enable(state, enable) {
    state.cprofilerEnabled = enable
  },
  debug_enable(state, enable) {
    state.debug = enable
  },
  set_input_state(state, value) {
    state.inputState = value
  },
  set_language(state, value) {
    if (state.currentLanguage !== value) {
      state.currentLanguage = value
    }
  },
  language_ready(state, ready) {
    state.isLanguageReady = ready
  },
  set_faq_vote(state, faq) {
    state.votedFaqs[faq.id] = true
  },
  update_selected_faq(state, faq) {
    state.selectedFaq = faq
  },
  update_related_faqs(state, faqs) {
    state.relatedFaqs = faqs
  },
  set_ie_status(state, isIE) {
    state.isIE = isIE
  },
  update_faq_search_results(state, faqs) {
    state.faqSearchResults = faqs
  },
  update_selected_faq_topic(state, topicId) {
    state.previouslySelectedFaqTopic = state.selectedFaqTopic
    state.selectedFaqTopic = topicId
  },
  set_cart(state, cart) {
    state.cart = cart
  },
  set_shipping_and_tax(state, data) {
    state.shipping = data.shipping
    state.tax = data.tax
  },
  set_cart_state(state, value) {
    state.cartIsOpen = value
  },
  force_loader_display(state, enable) {
    state.forceDisplayLoader = enable
  },
  toggle_loader_display(state, enable) {
    state.initialLoading = enable
  },
  refresh_session_on_undo(state, refresh) {
    state.refreshSessionOnUndo = refresh
  },
  set_session_started(state) {
    state.session.started = true
  },
  captcha_loaded(state) {
    state.captchaLoaded = true
  },
  fulfillment_loaded(state, fulfillments) {
    state.fulfillments = fulfillments
  },
  setShowChatbotHeader(state, payload) {
    state.showChatbotHeader = payload
  },
  set_subscription_only(state, isSubscriptionOnly) {
    state.subscriptionOnly = isSubscriptionOnly
  },
  // Step stack mutations
  push_to_step_stack(state, step) {
    state.stepStack.push(step)
  },
  pop_from_step_stack(state) {
    if (state.stepStack.length > 0) {
      state.stepStack.pop()
    }
    if (state.stepStack.length === 0) {
      state.undoCount = 0
    }
  },
  clear_step_stack(state) {
    state.stepStack = []
    state.undoCount = 0
  },
  remove_steps_from_step_stack(state, count) {
    if (count > 0 && state.stepStack.length >= count) {
      state.stepStack.splice(state.stepStack.length - count, count)
    }
  }
}

export default mutations
